import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useShellTranslation } from './useShellTranslation';
import { shellTokens } from './shellTokens';
import { useShell } from './ShellContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { Notification } from '../../services/userService';
import { getNotificationDestination } from '../../utils/notificationNavigation';
import { useOverlayFocus } from './useOverlayFocus';
import { formatUnreadCount, impersonationSubjectName } from './notificationsView';

const { color, radius, shadow, z } = shellTokens;

type Severity = 'error' | 'warn' | 'info';

const SEVERITY_STYLES: Record<Severity, { bg: string; dot: string }> = {
  error: { bg: color.dangerTint, dot: color.danger },
  warn: { bg: color.warningTint, dot: color.warning },
  info: { bg: color.infoTint, dot: color.accent },
};

/** The notification model carries a type, not a severity; this is the mapping. */
const severityOf = (notification: Notification): Severity => {
  switch (notification.type) {
    case 'integration_sync_failed':
    case 'map_violation':
    case 'account_suspended':
      return 'error';
    case 'map_pricing_update':
    case 'price_changed':
    case 'user_role_changed':
    case 'shopify_listing_hold':
      return 'warn';
    default:
      return 'info';
  }
};

const toDate = (value: any): Date | null => {
  try {
    const date = value?.toDate?.() ?? new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Relative time in the active locale — "12 min ago" / "il y a 12 min". */
const formatRelative = (date: Date | null, locale: string): string => {
  if (!date) return '';
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const elapsed = date.getTime() - Date.now();
  const magnitude = Math.abs(elapsed);
  if (magnitude < HOUR) return formatter.format(Math.round(elapsed / MINUTE), 'minute');
  if (magnitude < DAY) return formatter.format(Math.round(elapsed / HOUR), 'hour');
  return formatter.format(Math.round(elapsed / DAY), 'day');
};

const isToday = (date: Date | null) => {
  if (!date) return false;
  const now = new Date();
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      p: '10px 14px 4px',
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: color.faint,
    }}
  >
    {children}
  </Typography>
);

export const NotificationsMenu: React.FC = () => {
  const { t } = useShellTranslation();
  const navigate = useNavigate();
  const { overlay, closeOverlay } = useShell();
  const { language, region, formatNumber } = useLocale();
  // LocaleContext.locale is region-only ('en-CA' even for a French user), which
  // would leave English timestamps inside an otherwise French panel.
  const timeLocale = `${language}-${region}`;
  const {
    notifications,
    unreadNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    userProfile,
  } = useAuth();
  const { getEffectiveRole, isImpersonating, impersonatedOrg, impersonatedBrand, impersonatedRole } =
    useImpersonation();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const { containerRef, onKeyDown } = useOverlayFocus<HTMLDivElement>(
    overlay?.kind === 'notifications',
    // "Mark all read" is the first control but disappears the moment it is
    // used, which would drop focus to document.body and out of the trap.
    { focusContainer: true },
  );

  // Resolve destinations as whoever the session currently *is*. Using the real
  // admin profile while impersonating hands back /app/admin routes, which
  // AppShell then treats as leaving the workspace and ends the session — and
  // since the sidebar offers no admin links during View-as, clicking a
  // notification had become the main way to trip that exit by accident.
  const isAdmin = getEffectiveRole(userProfile) === 'admin';

  // Notifications are per-user documents (the Firestore rules let a user read
  // only their own), so during View-as the list loaded here is the admin's,
  // not the impersonated account's. Showing it under that account's banner
  // misattributes it, and "Mark all read" would clear the admin's real queue.
  // Until there is an org-scoped read, hide the list and every write.
  const hidden = isImpersonating;

  const { today, earlier } = useMemo(() => {
    const visible = filter === 'unread' ? notifications.filter((n) => !n.read) : notifications;
    return {
      today: visible.filter((n) => isToday(toDate(n.createdAt))),
      earlier: visible.filter((n) => !isToday(toDate(n.createdAt))),
    };
  }, [notifications, filter]);

  if (overlay?.kind !== 'notifications') return null;

  const open = (notification: Notification) => {
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }
    // A notification that doesn't lead anywhere is noise; fall back to the list.
    const destination = getNotificationDestination(notification, isAdmin) || '/app/notifications';
    closeOverlay();
    navigate(destination);
  };

  const renderRow = (notification: Notification) => {
    const severity = SEVERITY_STYLES[severityOf(notification)];
    const date = toDate(notification.createdAt);
    return (
      <ButtonBase
        key={notification.id}
        onClick={() => open(notification)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          p: '10px 14px',
          textAlign: 'left',
          '&:hover': { background: color.surface },
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            flexShrink: 0,
            borderRadius: radius.menuRow,
            background: severity.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: severity.dot }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '13.5px',
              fontWeight: notification.read ? 500 : 600,
              color: notification.read ? color.bodyAlt : color.ink,
            }}
          >
            {notification.title}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: color.muted }}>
            {notification.message}
          </Typography>
          <Typography sx={{ fontSize: '11.5px', color: color.faint, mt: '2px' }}>
            {formatRelative(date, timeLocale)}
          </Typography>
        </Box>
        {!notification.read && (
          <Box
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              mt: '5px',
              borderRadius: '50%',
              background: color.accent,
            }}
          />
        )}
      </ButtonBase>
    );
  };

  const tab = (value: 'all' | 'unread', label: string) => (
    <ButtonBase
      onClick={() => setFilter(value)}
      sx={{
        px: '10px',
        py: '4px',
        borderRadius: radius.menuRow,
        fontSize: '12.5px',
        fontWeight: 600,
        color: filter === value ? color.ink : color.muted,
        background: filter === value ? color.white : 'transparent',
        boxShadow: filter === value ? shadow.tab : 'none',
      }}
    >
      {label}
    </ButtonBase>
  );

  return (
    <>
      <Box
        onClick={closeOverlay}
        sx={{ position: 'fixed', inset: 0, zIndex: z.menu - 1 }}
        aria-hidden
      />
      <Box
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        aria-label={t('shell.notifications.title')}
        sx={{
          position: 'fixed',
          top: overlay.at.top,
          right: overlay.at.right,
          width: 382,
          maxWidth: 'calc(100vw - 24px)',
          // Header, tabs, list and footer together outrun a short or landscape
          // viewport — sooner still when the impersonation banner pushes the
          // bell down — and the shell clips whatever spills. Cap to the space
          // below the anchor and let the list, not the footer, give way.
          maxHeight: `calc(100vh - ${overlay.at.top + 12}px)`,
          display: 'flex',
          flexDirection: 'column',
          zIndex: z.menu,
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: radius.panel,
          boxShadow: shadow.notifications,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            p: '13px 14px 11px',
          }}
        >
          <Typography sx={{ flex: 1, fontSize: '14.5px', fontWeight: 700, color: color.ink }}>
            {t('shell.notifications.title')}
          </Typography>
          {!hidden && unreadNotifications > 0 && (
            <ButtonBase
              onClick={() => {
                // This button unmounts the moment the unread count hits zero.
                // Hand focus back to the panel first, or it lands on
                // document.body and escapes the trap.
                containerRef.current?.focus();
                markAllNotificationsAsRead();
              }}
              sx={{ fontSize: '12.5px', fontWeight: 600, color: color.accent }}
            >
              {t('shell.notifications.markAllRead')}
            </ButtonBase>
          )}
        </Box>

        {!hidden && (
          <Box sx={{ display: 'flex', gap: '4px', p: '4px 8px', background: color.surface }}>
            {tab('all', t('shell.notifications.all'))}
            {tab(
              'unread',
              `${t('shell.notifications.unread')} · ${formatUnreadCount(unreadNotifications, formatNumber)}`,
            )}
          </Box>
        )}

        <Box sx={{ flex: '1 1 auto', minHeight: 0, maxHeight: '46vh', overflowY: 'auto' }}>
          {hidden && (
            <Typography sx={{ p: '22px 14px', fontSize: '13px', color: color.faint }}>
              {t('shell.notifications.hiddenWhileViewingAs', {
                who: impersonationSubjectName(
                  { impersonatedOrg, impersonatedBrand, impersonatedRole },
                  t,
                ),
              })}
            </Typography>
          )}
          {!hidden && today.length === 0 && earlier.length === 0 && (
            <Typography sx={{ p: '22px 14px', fontSize: '13px', color: color.faint }}>
              {t('shell.notifications.empty')}
            </Typography>
          )}
          {!hidden && today.length > 0 && (
            <>
              <GroupLabel>{t('shell.notifications.today')}</GroupLabel>
              {today.map(renderRow)}
            </>
          )}
          {!hidden && earlier.length > 0 && (
            <>
              <GroupLabel>{t('shell.notifications.earlier')}</GroupLabel>
              {earlier.map(renderRow)}
            </>
          )}
        </Box>

        {/* The full page lists the same per-user documents, so it goes too. */}
        {!hidden && (
          <ButtonBase
            onClick={() => {
              closeOverlay();
              navigate('/app/notifications');
            }}
            sx={{
              width: '100%',
              flex: '0 0 auto',
              p: '10px',
              background: color.surface,
              borderTop: `1px solid ${color.lineSubtle}`,
              fontSize: '13px',
              fontWeight: 600,
              color: color.accent,
            }}
          >
            {t('shell.notifications.viewAll')}
          </ButtonBase>
        )}
      </Box>
    </>
  );
};
